import { toast } from "react-toastify";

import {
  AdoptAgenticLogicRuleGeneratedTestsConflictSchema,
  AdoptAgenticLogicRuleGeneratedTestsRequestBodyType,
  AdoptAgenticLogicRuleGeneratedTestsResponseBodyType,
  GetAgenticLogicRuleDraftResponseBodyType,
  GetAgenticLogicRuleGeneratedTestsResponseBodyType,
} from "@intelligems/ig-types";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";
import { getAgenticLogicConflict } from "./agentic-logic-conflict";

const adoptAgenticLogicRuleGeneratedTests = async (
  payload: ApiRequest<
    AdoptAgenticLogicRuleGeneratedTestsRequestBodyType,
    { ruleId: string }
  >
) => {
  const resp = await api.post<
    AdoptAgenticLogicRuleGeneratedTestsResponseBodyType,
    AdoptAgenticLogicRuleGeneratedTestsRequestBodyType
  >(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/adopt-generated-tests`,
    payload.body
  );
  return resp.data;
};

/**
 * Moves generated tests into the user suite ("Edit as my test"). The server
 * writes both suites onto the draft, so the response seeds the draft and
 * generated-tests caches rather than invalidating them — a refetch would race
 * the editor reseeding from exactly this data. A read of either key already
 * in flight (the save that preceded the adopt invalidated the inventory) is
 * cancelled first, or its stale answer would land over the seed.
 */
export const useAdoptAgenticLogicRuleGeneratedTests = () => {
  const queryClient = useQueryClient();

  return useMutation<
    AdoptAgenticLogicRuleGeneratedTestsResponseBodyType,
    Error,
    ApiRequest<
      AdoptAgenticLogicRuleGeneratedTestsRequestBodyType,
      { ruleId: string }
    >,
    unknown
  >({
    mutationFn: async (data) => adoptAgenticLogicRuleGeneratedTests(data),
    onSuccess: async (data, payload) => {
      const { ruleId } = payload.pathParams;
      await Promise.all([
        queryClient.cancelQueries({
          queryKey: agenticLogicKeys.generatedTests(ruleId),
        }),
        queryClient.cancelQueries({ queryKey: agenticLogicKeys.draft(ruleId) }),
      ]);
      queryClient.setQueryData<GetAgenticLogicRuleDraftResponseBodyType>(
        agenticLogicKeys.draft(ruleId),
        { draft: data.draft }
      );
      queryClient.setQueryData<GetAgenticLogicRuleGeneratedTestsResponseBodyType>(
        agenticLogicKeys.generatedTests(ruleId),
        {
          suite: data.draft.generatedTestSuite ?? null,
          tests: data.generatedTests,
          // An explicit generated-suite write clears the reuse fingerprint;
          // the next run re-verifies or regenerates the suite.
          fingerprintCurrent: false,
        }
      );
    },
    onError: (error, payload) => {
      const { ruleId } = payload.pathParams;
      // Branches on `reason`, never on the body's shape.
      const conflict = getAgenticLogicConflict(
        error,
        AdoptAgenticLogicRuleGeneratedTestsConflictSchema
      );
      switch (conflict?.reason) {
        case "stale_draft":
          // The draft was written since the editor loaded its inventory —
          // a save, a run, another adoption. Reload both and let the author
          // choose the test again from what is there now.
          void queryClient.invalidateQueries({
            queryKey: agenticLogicKeys.generatedTests(ruleId),
          });
          void queryClient.invalidateQueries({
            queryKey: agenticLogicKeys.draft(ruleId),
          });
          toast.error("The draft changed — reloaded, try again.");
          return;
        case "unknown_test_ids":
          // The suite was regenerated (or other tests moved) since the editor
          // loaded it, so the ids it offered no longer exist.
          void queryClient.invalidateQueries({
            queryKey: agenticLogicKeys.generatedTests(ruleId),
          });
          toast.error("AI tests were regenerated, try again.");
          return;
        case undefined: {
          const errorDetail = getMutationErrorMessage(error);
          toast.error(
            `Failed to move the test into your tests${
              errorDetail ? `. ${errorDetail}` : ""
            }`
          );
        }
      }
    },
  });
};
